<template>
  <div :class="$style.custom_file_upload">
    <label
      :class="[
        $style.label,
        { [$style.is_dragging]: state.isDragging, [$style.is_selected]: state.mediaSrc.length > 0 },
      ]"
      @dragover.prevent="switchDragState('over')"
      @dragleave="switchDragState('leave')"
    >
      {{ state.mediaSrc.length === 0 ? placeholderButtonText : placeholderSelectedText }}
      <input id="multi-input" :class="$style.file" type="file" :accept="acceptType" @change="onFileChange($event)" />
    </label>
    <div v-if="state.errorMessage" :class="$style.error_message">{{ state.errorMessage }}</div>
    <template v-if="state.mediaSrc.length > 0">
      <div :class="$style.thumbs_wrapper">
        <div v-for="(src, _index) in state.mediaSrc" :key="`src_${_index}`" :class="$style.thumb">
          <div :class="$style.image_container">
            <div v-if="enableRemoveButton" :class="$style.img_remove_btn" @click="removeImage(_index)" />
            <AtomsConsumerImage :src="src" alt="サムネイル" :width="300" :height="300" />
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { EventHandler, ElementEvent } from "~/types/events"

type MediaType = "Excel" | "Word" | "Image" | "Video" | "Pdf" | "Audio" | "Video" | "ImageOrVideo"

const props = withDefaults(
  defineProps<{
    mediaType?: MediaType
    mediaSrc?: string[]
    placeholderButtonText?: string
    placeholderSelectedText?: string
    enableRemoveButton?: boolean
    maxFileCount?: number
  }>(),
  {
    mediaType: "Image",
    mediaSrc: () => [],
    placeholderButtonText: "ファイルを選択",
    placeholderSelectedText: "ファイル選択済み",
    enableRemoveButton: true,
    maxFileCount: 10,
  },
)

const emit = defineEmits<{
  onRemove: [index: number]
  onChange: [file: File, url: string]
}>()

const acceptType = computed(() => {
  if (props.mediaType === "Excel") {
    return ".xlsx, .xls"
  } else if (props.mediaType === "Word") {
    return ".doc, .docx"
  } else if (props.mediaType === "ImageOrVideo") {
    return "video/*,image/*"
  } else if (props.mediaType === "Image") {
    return "image/*"
  } else if (props.mediaType === "Video") {
    return "video/*"
  } else if (props.mediaType === "Pdf") {
    return "application/pdf"
  } else if (props.mediaType === "Audio") {
    return "audio/*"
  } else {
    return "*"
  }
})

const state = reactive({
  mediaSrc: props.mediaSrc,
  isDragging: false,
  errorMessage: "",
})

watch(
  () => props.mediaSrc,
  (newVal) => {
    state.mediaSrc = newVal
  },
  { immediate: true },
)

const switchDragState = (_type: string) => {
  if (_type === "over") {
    state.isDragging = true
  } else {
    state.isDragging = false
  }
}

const onFileChange: EventHandler = (e: ElementEvent<HTMLInputElement>) => {
  const files = e.target?.files
  if (!files?.length) return
  if (state.mediaSrc.length + files.length > props.maxFileCount) {
    state.errorMessage = `アップロードできる最大ファイル数は${props.maxFileCount}つです`
    return
  }
  state.errorMessage = ""
  // Todo: 複数のファイルを選択できるようにする
  const file = files[0]
  const reader = new FileReader()
  reader.onload = (e) => {
    const src = String(e.target?.result)
    emit("onChange", file, src)
  }
  reader.readAsDataURL(file)
}

const removeImage = (index: number) => {
  emit("onRemove", index)
  state.errorMessage = ""
}
</script>

<style scoped module lang="scss">
.custom_file_upload {
  .label {
    width: 100%;
    -webkit-appearance: none;
    appearance: none;
    font-size: $font-size-16;
    color: $primary;
    padding: 0.75rem 1rem;
    background-color: $secondary-10;
    border: 1px solid $primary-40;
    border-radius: 0.25rem;
    transition: all 0.2s ease;
    position: relative;
    cursor: pointer;
    &::placeholder {
      color: $primary-40;
    }
    input {
      display: none;
    }
    &.is_selected {
      background-color: $primary-20;
      border-color: $primary-40;
      color: $text-black;
      &:hover {
        background-color: rgba($primary, 0.3);
      }
      &::before {
        position: absolute;
        top: calc(50% - 0.375rem);
        right: 0.75rem;
        transform: rotate(50deg);
        width: 0.45rem;
        height: 0.75rem;
        border-right: 2px solid $primary-40;
        border-bottom: 2px solid $primary-40;
        content: "";
      }
    }
    &.is_dragging {
      background-color: $primary-40;
    }
  }
  .error_message {
    font-size: $font-size-12;
    color: $danger;
  }
  .thumbs_wrapper {
    display: flex;
    flex-wrap: wrap;
    .thumb {
      margin: 1rem 1rem 0 0;
    }
    .image_container {
      position: relative;

      & img {
        height: 100px;
        width: 100px;
        object-fit: contain;
      }
    }
    .img_remove_btn {
      position: absolute;
      top: -10px;
      right: -10px;
      display: flex;
      justify-content: center;
      width: 26px;
      height: 26px;
      cursor: pointer;
      background: $black-10;
      border-radius: 50%;

      @include md {
        top: -5px;
        right: -5px;
      }

      &::before {
        width: 100%;
        height: 26px;
        content: "";
        background: url("/images/common/modal/close.svg") no-repeat center;
        background-size: 58%;
      }
    }
  }
}
</style>
